import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  createDepartment,
  fetchDepartments,
} from "src/features/departmentsSlice";
import { AppDispatch } from "src/store";
import { useTranslation } from "react-i18next";

interface CreateDepartmentModalProps {
  open: boolean;
  onClose: () => void;
}

interface DepartmentFormInputs {
  name: string;
  location?: string;
  team?: string;
  industry?: string;
}

const CreateDepartmentModal: React.FC<CreateDepartmentModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DepartmentFormInputs>({
    defaultValues: {
      name: "",
      location: "",
      team: "",
      industry: "",
    },
  });

  const onSubmit: SubmitHandler<DepartmentFormInputs> = (data) => {
    dispatch(createDepartment(data)).then((action) => {
      if (createDepartment.fulfilled.match(action)) {
        dispatch(fetchDepartments());
        onClose();
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t("departments.create.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {t("departments.create.description")}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t("departments.create.name")}
            {...register("name", { required: t("departments.create.nameRequired") })}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : null}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t("departments.create.location")}
            {...register("location")}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t("departments.create.team")}
            {...register("team")}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t("departments.create.industry")}
            {...register("industry")}
            sx={{ mt: 2 }}
          />
          <DialogActions>
            <Button onClick={onClose}>{t("departments.create.cancel")}</Button>
            <Button type="submit" variant="contained">
              {t("departments.create.continue")}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDepartmentModal;
